import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import styles from './IndustryDashboard.module.css';

const ExampleIndustryDashboard = () => {
  const { user, userRole, logout } = useAuth();
  const [selectedSector, setSelectedSector] = useState(null);
  const [timeRange, setTimeRange] = useState('month'); // month, quarter, year

  // Sektör verileri (dummy data)
  const sectors = [
    { id: 'textile', name: 'Tekstil', color: '#3b82f6' },
    { id: 'food', name: 'Gıda', color: '#10b981' },
    { id: 'plastic', name: 'Plastik', color: '#f59e0b' },
    { id: 'marble', name: 'Mermer', color: '#8b5cf6' },
    { id: 'metal', name: 'Metal İşleme', color: '#ef4444' },
    { id: 'chemical', name: 'Kimya', color: '#06b6d4' },
    { id: 'paper', name: 'Kağıt', color: '#84cc16' },
    { id: 'automotive', name: 'Otomotiv', color: '#ec4899' }
  ];

  // Dummy data - sektör bazlı su tüketimi
  const getSectorData = (sectorId) => {
    const baseData = {
      textile: { totalConsumption: 1250000, facilities: 45, avgPerFacility: 27778, trend: 5.2 },
      food: { totalConsumption: 980000, facilities: 32, avgPerFacility: 30625, trend: -2.1 },
      plastic: { totalConsumption: 750000, facilities: 28, avgPerFacility: 26786, trend: 8.5 },
      marble: { totalConsumption: 420000, facilities: 18, avgPerFacility: 23333, trend: -1.3 },
      metal: { totalConsumption: 680000, facilities: 25, avgPerFacility: 27200, trend: 3.7 },
      chemical: { totalConsumption: 1100000, facilities: 22, avgPerFacility: 50000, trend: -4.2 },
      paper: { totalConsumption: 540000, facilities: 20, avgPerFacility: 27000, trend: 6.1 },
      automotive: { totalConsumption: 890000, facilities: 30, avgPerFacility: 29667, trend: 2.4 }
    };
    return baseData[sectorId] || { totalConsumption: 0, facilities: 0, avgPerFacility: 0, trend: 0 };
  };

  // Toplam metrikler
  const totalConsumption = sectors.reduce((sum, sector) => {
    return sum + getSectorData(sector.id).totalConsumption;
  }, 0);

  const totalFacilities = sectors.reduce((sum, sector) => {
    return sum + getSectorData(sector.id).facilities;
  }, 0);

  // Aylık trend verileri (son 12 ay) - Her sektör için çok farklı trend
  const getMonthlyTrend = (sectorId) => {
    const base = getSectorData(sectorId).totalConsumption;
    const months = [];
    // Her sektör için çok farklı trend pattern'leri
    const sectorTrends = {
      textile: { start: 0.70, end: 1.30, volatility: 0.25, pattern: 'upward' },
      food: { start: 1.20, end: 0.80, volatility: 0.20, pattern: 'downward' },
      plastic: { start: 0.60, end: 1.40, volatility: 0.30, pattern: 'upward' },
      marble: { start: 0.90, end: 1.10, volatility: 0.15, pattern: 'stable' },
      metal: { start: 0.85, end: 1.25, volatility: 0.22, pattern: 'upward' },
      chemical: { start: 0.50, end: 1.50, volatility: 0.35, pattern: 'volatile' },
      paper: { start: 1.10, end: 0.90, volatility: 0.18, pattern: 'downward' },
      automotive: { start: 0.75, end: 1.35, volatility: 0.28, pattern: 'upward' }
    };
    
    const trend = sectorTrends[sectorId] || { start: 0.90, end: 1.10, volatility: 0.15, pattern: 'stable' };
    
    for (let i = 11; i >= 0; i--) {
      const date = new Date();
      date.setMonth(date.getMonth() - i);
      const progress = (11 - i) / 11;
      
      let linearTrend;
      if (trend.pattern === 'downward') {
        linearTrend = trend.start - (trend.start - trend.end) * progress;
      } else if (trend.pattern === 'volatile') {
        linearTrend = 1.0 + (Math.sin(progress * Math.PI * 2) * 0.3);
      } else if (trend.pattern === 'stable') {
        linearTrend = 1.0 + (Math.random() - 0.5) * 0.1;
      } else {
        linearTrend = trend.start + (trend.end - trend.start) * progress;
      }
      
      const seasonalVariation = Math.sin(i * Math.PI / 4) * trend.volatility;
      const randomVariation = (Math.random() - 0.5) * 0.15;
      const finalValue = base * (linearTrend + seasonalVariation + randomVariation);
      
      months.push({
        month: date.toLocaleDateString('tr-TR', { month: 'short', year: '2-digit' }),
        value: Math.max(finalValue, base * 0.4) // Minimum %40
      });
    }
    return months;
  };

  // Sektör detay sayfası
  const renderSectorDetail = () => {
    if (!selectedSector) return null;

    const sector = sectors.find(s => s.id === selectedSector);
    const data = getSectorData(selectedSector);
    const monthlyData = getMonthlyTrend(selectedSector);
    const maxValue = Math.max(...monthlyData.map(m => m.value));

    return (
      <div className={styles.detailModal}>
        <div className={styles.detailContent}>
          <div className={styles.detailHeader}>
            <h2>{sector.name} Sektörü Detayları</h2>
            <button className={styles.closeButton} onClick={() => setSelectedSector(null)}>×</button>
          </div>
          
          <div className={styles.detailGrid}>
            <div className={styles.detailCard}>
              <div className={styles.detailCardLabel}>Toplam Tüketim</div>
              <div className={styles.detailCardValue}>
                {(data.totalConsumption / 1000).toFixed(1)}k L
              </div>
            </div>
            <div className={styles.detailCard}>
              <div className={styles.detailCardLabel}>Tesis Sayısı</div>
              <div className={styles.detailCardValue}>{data.facilities}</div>
            </div>
            <div className={styles.detailCard}>
              <div className={styles.detailCardLabel}>Tesis Başına Ortalama</div>
              <div className={styles.detailCardValue}>
                {(data.avgPerFacility / 1000).toFixed(1)}k L
              </div>
            </div>
            <div className={styles.detailCard}>
              <div className={styles.detailCardLabel}>Trend</div>
              <div className={styles.detailCardValue} style={{ 
                color: data.trend > 0 ? '#10b981' : '#ef4444' 
              }}>
                {data.trend > 0 ? '↑' : '↓'} {Math.abs(data.trend)}%
              </div>
            </div>
          </div>

          <div className={styles.chartCard}>
            <h3>Aylık Tüketim Trendi</h3>
            <div className={styles.chartContainer}>
              <div className={styles.barChart}>
                {monthlyData.map((month, idx) => {
                  const percentage = (month.value / maxValue) * 100;
                  return (
                    <div key={idx} className={styles.barWrapper}>
                      <div className={styles.barValue}>
                        {(month.value / 1000).toFixed(0)}k
                      </div>
                      <div 
                        className={styles.bar}
                        style={{ 
                          height: `${percentage}%`,
                          background: `linear-gradient(180deg, ${sector.color} 0%, ${sector.color}dd 100%)`
                        }}
                        onMouseEnter={(e) => {
                          const tooltip = e.currentTarget.parentElement.querySelector(`.${styles.tooltip}`);
                          if (tooltip) {
                            tooltip.style.display = 'block';
                            tooltip.textContent = `${month.month}: ${month.value.toLocaleString('tr-TR')} L`;
                          }
                        }}
                        onMouseLeave={(e) => {
                          const tooltip = e.currentTarget.parentElement.querySelector(`.${styles.tooltip}`);
                          if (tooltip) tooltip.style.display = 'none';
                        }}
                      >
                        <div className={styles.tooltip}></div>
                      </div>
                      <div className={styles.barLabel}>{month.month}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.dashboard}>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', maxHeight: '100vh', overflow: 'hidden' }}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}>Sanayi Su Tüketim Dashboard</h1>
          <p className={styles.subtitle}>Sektör Bazlı Su Tüketim Analizi</p>
        </div>
        <div className={styles.headerRight}>
          <button className={styles.logoutButton} onClick={logout}>
            Çıkış Yap
          </button>
        </div>
      </div>

      {/* Top Metrics */}
      <div className={styles.metricsGrid}>
        <div className={styles.metricCard}>
          <div className={styles.metricLabel}>Toplam Su Tüketimi</div>
          <div className={styles.metricValue}>
            {(totalConsumption / 1000000).toFixed(2)}M L
          </div>
          <div className={styles.metricChange}>Bu Ay</div>
        </div>
        <div className={styles.metricCard}>
          <div className={styles.metricLabel}>Toplam Tesis Sayısı</div>
          <div className={styles.metricValue}>{totalFacilities}</div>
          <div className={styles.metricChange}>Aktif Tesisler</div>
        </div>
        <div className={styles.metricCard}>
          <div className={styles.metricLabel}>Ortalama Tesis Tüketimi</div>
          <div className={styles.metricValue}>
            {((totalConsumption / totalFacilities) / 1000).toFixed(1)}k L
          </div>
          <div className={styles.metricChange}>Tesis Başına</div>
        </div>
      </div>

      {/* Ana İçerik: Sektörler Sol, Grafik Sağ */}
      <div className={styles.mainContent}>
        {/* Sektör Kartları - Sol */}
        <div className={styles.sectorsSection}>
          <div className={styles.sectorsGrid}>
            {sectors.map((sector) => {
              const data = getSectorData(sector.id);
              const percentage = (data.totalConsumption / totalConsumption) * 100;
              
              return (
                <div 
                  key={sector.id} 
                  className={styles.sectorCard}
                  onClick={() => setSelectedSector(sector.id)}
                >
                  <div className={styles.sectorHeader}>
                    <div className={styles.sectorInfo}>
                      <h3 className={styles.sectorName}>{sector.name}</h3>
                      <div className={styles.sectorFacilities}>{data.facilities} Tesis</div>
                    </div>
                    <div className={styles.sectorTrend} style={{ color: data.trend > 0 ? '#10b981' : '#ef4444' }}>
                      {data.trend > 0 ? '↑' : '↓'} {Math.abs(data.trend)}%
                    </div>
                  </div>
                  
                  <div className={styles.sectorMetrics}>
                    <div className={styles.sectorMetric}>
                      <div className={styles.sectorMetricLabel}>Toplam</div>
                      <div className={styles.sectorMetricValue}>
                        {(data.totalConsumption / 1000).toFixed(1)}k L
                      </div>
                    </div>
                    <div className={styles.sectorMetric}>
                      <div className={styles.sectorMetricLabel}>Ortalama</div>
                      <div className={styles.sectorMetricValue}>
                        {(data.avgPerFacility / 1000).toFixed(1)}k L
                      </div>
                    </div>
                  </div>

                  <div className={styles.sectorProgress}>
                    <div 
                      className={styles.sectorProgressBar}
                      style={{ 
                        width: `${percentage}%`
                      }}
                    />
                  </div>
                  <div className={styles.sectorPercentage}>
                    %{percentage.toFixed(1)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Sektör Karşılaştırma Grafiği - Sağ */}
        <div className={styles.chartSection}>
          <div className={styles.chartCard}>
            <h3>Sektör Bazlı Tüketim</h3>
            <div className={styles.chartContainer}>
              <div className={styles.horizontalBarChart}>
                {sectors
                  .map(sector => ({
                    ...sector,
                    data: getSectorData(sector.id)
                  }))
                  .sort((a, b) => b.data.totalConsumption - a.data.totalConsumption)
                  .map((sector, idx) => {
                    const percentage = (sector.data.totalConsumption / totalConsumption) * 100;
                    return (
                      <div key={sector.id} className={styles.horizontalBarRow}>
                        <div className={styles.barRowLabel}>{sector.name}</div>
                        <div className={styles.barRowContainer}>
                          <div 
                            className={styles.horizontalBar}
                            style={{ 
                              width: `${percentage}%`
                            }}
                            onMouseEnter={(e) => {
                              const tooltip = e.currentTarget.querySelector(`.${styles.tooltip}`);
                              if (tooltip) {
                                tooltip.style.display = 'block';
                                tooltip.textContent = `${sector.data.totalConsumption.toLocaleString('tr-TR')} L`;
                              }
                            }}
                            onMouseLeave={(e) => {
                              const tooltip = e.currentTarget.querySelector(`.${styles.tooltip}`);
                              if (tooltip) tooltip.style.display = 'none';
                            }}
                          >
                            <div className={styles.tooltip}></div>
                          </div>
                          <div className={styles.barRowValue}>
                            {(sector.data.totalConsumption / 1000).toFixed(1)}k
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Detay Modal */}
      {renderSectorDetail()}
      </div>
    </div>
  );
};

export default ExampleIndustryDashboard;
